/**
 * CG Boost Metric Translation Layer
 *
 * Single source of truth for the dual-language metric display used across
 * every CG Boost view: each metric renders the social-strategy label first
 * with the paid-media label as smaller secondary text.
 */

export type MetricKey =
  | 'impressions'
  | 'views'
  | 'vtr'
  | 'completionRate'
  | 'hookRate'
  | 'ctr'
  | 'cpm'
  | 'cpv'
  | 'frequency'
  | 'spendPacing';

export interface MetricLabelEntry {
  social: string;
  paid: string;
}

export const METRIC_TRANSLATION: Record<MetricKey, MetricLabelEntry> = {
  impressions:    { social: 'Reach',                  paid: 'Impressions' },
  views:          { social: 'Audience Exposure',      paid: 'Views' },
  vtr:            { social: 'Audience Attention',     paid: 'View-Through Rate (VTR)' },
  completionRate: { social: 'Viewer Retention',       paid: 'Watch Completion Rate' },
  hookRate:       { social: 'Hook Strength',          paid: 'Hook Rate (0–3s retention)' },
  ctr:            { social: 'Audience Action',        paid: 'Click-Through Rate (CTR)' },
  cpm:            { social: 'Distribution Efficiency', paid: 'CPM' },
  cpv:            { social: 'Cost Per Viewer',        paid: 'CPV' },
  frequency:      { social: 'Ad Frequency',           paid: 'Frequency' },
  spendPacing:    { social: 'Budget Deployment Rate', paid: 'Spend Pacing' },
};

export const METRIC_FORMAT: Record<MetricKey, 'integer' | 'percent' | 'currency' | 'currency4' | 'decimal'> = {
  impressions: 'integer',
  views: 'integer',
  vtr: 'percent',
  completionRate: 'percent',
  hookRate: 'percent',
  ctr: 'percent',
  cpm: 'currency',
  cpv: 'currency4',
  frequency: 'decimal',
  spendPacing: 'percent',
};

export function formatMetricValue(key: MetricKey, value: number): string {
  switch (METRIC_FORMAT[key]) {
    case 'integer':
      return Math.round(value).toLocaleString();
    case 'percent':
      return `${(value * 100).toFixed(1)}%`;
    case 'currency':
      return `$${value.toFixed(2)}`;
    case 'currency4':
      return `$${value.toFixed(4)}`;
    case 'decimal':
      return value.toFixed(2);
    default:
      return String(value);
  }
}
